import { findLostEntitiesIds } from 'tools/utils/entityUtils'
import React, { useEffect, useState } from 'react'
import { EntityTypeEnum } from 'common/enums/entityTypeEnum'
import { useActions } from 'common/hooks/useActions'
import { EntitiesStructure } from 'common/types/Page'
import { BaseEntityWithChildIdsInterface } from 'common/types/entities/EntityInterface'
import { OldEntityWithChildIds } from 'common/types/entities/OldEntityInterface'
import {
  getParentEntityTreeWithFixedParentId,
  getEntitiesByType,
  isWithChildIds,
  getEntityExistingChildIds,
  isEntityWithChildIds,
} from 'common/utils/entityUtils'
import { usePage } from 'client/store'
import { fixEntities, updateEntities } from 'client/store/page/pageActions'
import {
  getPageType,
  getReadableRootEntity,
} from 'client/store/page/pageSelectors'
import { LanguageSwitcher } from 'client/components/core/LanguageSwitcher'
import {
  ButtonSizeEnum,
  CloseButton,
  PrimaryButton,
  TertiaryButton,
} from 'src/editor/shared/ui-lib/buttons'
import { useAceEditor } from 'src/editor/shared/hooks/use-ace-editor'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { useIsImpersonatorAndSuperAdmin } from 'client/hooks/useIsImpersonatorAndSuperAdmin'

export const SidebarDevTools = () => {
  const [hideDevTools, setHideDevTools] = useState(false)

  const dispatch = useAppDispatch()

  const fixEntitiesAction = useActions(fixEntities)
  const entities = usePage(page => page.entities)
  const pageType = usePage(getPageType)
  const rootEntity = usePage(getReadableRootEntity)

  const [isDevToolsVisible, setIsDevToolsVisible] = useState(false)

  const isImpersonatorAndSuperAdmin = useIsImpersonatorAndSuperAdmin()

  const { AceEditor, onOpenCodeEditor } = useAceEditor()

  useEffect(() => {
    if (isImpersonatorAndSuperAdmin) {
      setIsDevToolsVisible(true)
    }
  }, [isImpersonatorAndSuperAdmin])

  function addParentId() {
    if (!rootEntity) {
      console.log('Root entity not found')
      return
    }

    const rootEntityTree = getParentEntityTreeWithFixedParentId(
      entities,
      rootEntity as OldEntityWithChildIds | BaseEntityWithChildIdsInterface,
    )
    const popups = getEntitiesByType(entities, EntityTypeEnum.Popup)
    const popupsWithEntities = {} as EntitiesStructure
    Object.values(popups).forEach(cur => {
      if (isWithChildIds(cur)) {
        Object.assign(
          popupsWithEntities,
          getParentEntityTreeWithFixedParentId(entities, cur),
        )
      }
    })

    const fixedStructure = {
      [rootEntity.id]: rootEntity,
      ...rootEntityTree,
      ...popupsWithEntities,
      ...popups,
    }

    fixEntitiesAction(fixedStructure)
  }

  function removeLostChildren() {
    const filteredEntities = {} as EntitiesStructure

    Object.values(entities).forEach(entity => {
      if (isEntityWithChildIds(entity)) {
        filteredEntities[entity.id] = {
          ...entity,
          childIds: getEntityExistingChildIds(entities, entity),
        }
      } else {
        filteredEntities[entity.id] = entity
      }
    })

    const lostEntitiesIds = findLostEntitiesIds(filteredEntities, pageType)

    lostEntitiesIds.forEach(entityId => {
      delete entities[entityId]
    })

    fixEntitiesAction(filteredEntities)
  }

  function editEntities(value: string | undefined) {
    dispatch(updateEntities(JSON.parse(value || '')))
  }

  return (
    isDevToolsVisible &&
    !hideDevTools && (
      <div className="flex w-full gap-0.5">
        <LanguageSwitcher />
        <PrimaryButton size={ButtonSizeEnum.Small} onClick={addParentId}>
          Fix parents
        </PrimaryButton>
        <PrimaryButton size={ButtonSizeEnum.Small} onClick={removeLostChildren}>
          Remove lost children
        </PrimaryButton>
        <TertiaryButton size={ButtonSizeEnum.Small} onClick={onOpenCodeEditor}>
          Edit entities
        </TertiaryButton>
        <CloseButton
          className="mr-[-17px]! self-start"
          onClick={() => setHideDevTools(true)}
        />
        {AceEditor && (
          <AceEditor
            withoutLinkHelpPage
            label="Entities"
            value={JSON.stringify(entities, null, 2)}
            handleChange={editEntities}
            ignoreAnnotationsAndValidation
          />
        )}
      </div>
    )
  )
}
